import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, KeyRound, Plus, ShieldCheck } from 'lucide-react';
import { gauntlet, ApiError } from '../lib/api';
import { Button, Callout, CopyButton, Panel, Skeleton } from '../components/ui';

export function ReceivePage() {
  const { accountId } = useParams();
  const id = Number(accountId);

  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['receive', id],
    queryFn: () => gauntlet.receiveAddress(id),
    retry: false,
  });

  const { data: wallets } = useQuery({ queryKey: ['wallets'], queryFn: () => gauntlet.wallets() });
  const wallet = wallets?.wallets.find((w) => w.accounts.some((a) => a.id === id));

  /* "Generate a new address" — deriving past the current window needs the
     vault open, which is why the failure below is usually a locked wallet
     rather than a broken account. */
  const generate = useMutation({
    mutationFn: () => gauntlet.receiveAddress(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receive', id] });
      await queryClient.invalidateQueries({ queryKey: ['addresses', id] });
    },
  });

  const { data: addressList } = useQuery({
    queryKey: ['addresses', id],
    queryFn: () => gauntlet.addresses(id, 0),
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  /* This used to render "No address available." for every possible cause,
     which told the user nothing and offered no way forward. Say what actually
     went wrong, and put the fix next to it. */
  if (!data) {
    const reason = error instanceof ApiError ? error.message : null;
    const locked = wallet && !wallet.unlocked;
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink">
          <ArrowLeft size={15} />
          Back
        </Link>
        <Panel title="Cannot show an address">
          <div className="space-y-3">
            <Callout tone={locked ? 'warn' : 'danger'} title={locked ? 'This wallet is locked' : 'No address yet'}>
              {locked
                ? 'Deriving an address needs the vault open, because the address comes from your seed. Unlock the wallet and this page will fill in.'
                : (reason ??
                  'This account has no addresses derived yet. That normally means the chain was added while the wallet was locked.')}
            </Callout>
            <div className="flex gap-2">
              {wallet && (
                <Link to={`/wallets/${wallet.id}`} className="flex-1">
                  <Button variant="primary" full icon={<KeyRound size={14} />}>
                    {locked ? 'Unlock wallet' : 'Open wallet'}
                  </Button>
                </Link>
              )}
              <Button full onClick={() => void refetch()}>
                Try again
              </Button>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  const used = (addressList?.addresses ?? []).filter((a) => a.used === 1).length;

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink">
        <ArrowLeft size={15} />
        Back
      </Link>

      <Panel title="Receive">
        <div className="flex flex-col items-center gap-5">
          {/* White plate behind the QR: scanners struggle with inverted codes,
              and a dark-on-dark QR is a genuine usability failure. */}
          <div className="rounded-xl bg-white p-4">
            <QRCodeSVG value={data.uri} size={208} level="M" marginSize={0} />
          </div>

          <div className="w-full space-y-2 text-center">
            <p className="label-xs">Your address</p>
            {/* Shown in full, never elided. Address-poisoning attacks rely on
                matching first and last characters, so a truncated form is not
                something a user can verify against. */}
            <p className="addr rounded-lg border border-hairline bg-abyss p-3 text-ink">
              {data.address.address}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <CopyButton value={data.address.address} label="Copy address" />
              <CopyButton value={data.uri} label="Copy payment link" />
              {/* Explicit rotation. The address advances on its own once one is
                  used, but wanting a fresh one before that is a legitimate and
                  previously unserved request. */}
              <Button
                size="sm"
                icon={<Plus size={13} />}
                loading={generate.isPending}
                disabled={wallet ? !wallet.unlocked : false}
                onClick={() => generate.mutate()}
              >
                New address
              </Button>
            </div>
            {wallet && !wallet.unlocked && (
              <p className="text-[11px] text-ink-faint">
                Unlock the wallet to derive a new address.
              </p>
            )}
          </div>

          <div className="w-full space-y-1 rounded-lg border border-hairline bg-abyss p-3 text-xs">
            <Row label="Derivation path" value={data.address.derivation_path} mono />
            <Row label="Index" value={String(data.address.address_index)} />
            <Row label="Type" value={data.address.change === 0 ? 'Receive' : 'Change'} />
          </div>

          <Callout tone="info" title="A fresh address each time">
            Gauntlet hands out a new address for every payment. Reusing one links all your payments to
            a single public identity that anyone can look up on a block explorer. Old addresses keep
            working — nothing is lost by rotating.
            {used > 0 && ` ${used} address${used === 1 ? ' has' : 'es have'} been used so far.`}
          </Callout>

          <div className="flex w-full items-start gap-2 text-xs text-ink-faint">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-gain" />
            <span>
              Check the first and last several characters against the QR before sending a large amount
              from another device.
            </span>
          </div>
        </div>
      </Panel>

      {(addressList?.addresses.length ?? 0) > 1 && (
        <Panel title="All receive addresses" padded={false}>
          <ul className="divide-y divide-hairline">
            {addressList?.addresses.map((address) => (
              <li key={address.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="num w-6 text-xs text-ink-faint">{address.address_index}</span>
                <span className="addr min-w-0 flex-1 truncate">{address.address}</span>
                {address.used === 1 && <span className="text-[11px] text-ink-faint">used</span>}
                <CopyButton value={address.address} label="" />
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-ink-faint">{label}</span>
      <span className={mono ? 'font-mono text-ink-dim' : 'text-ink-dim'}>{value}</span>
    </div>
  );
}
